import React, { useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDealershipStore } from '../../../store/zustand/dealershipStore'
import { DateRangePicker, Table } from '@demandlocal/components'
import { Dealership } from '../../../lib/types/Dealership'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import { useSelector } from 'react-redux'
import { ReactComponent as ActionEdit } from '../../../images/icons/action_edit.svg'
import { useGetAgencyProfileDealerships } from '../../../api/agencies/useGetAgencyProfileDealerships'
import { useDateRange } from '../../../store/zustand/dateRangeStore'
import { DateRange } from '../../../lib/types/DateRange'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useGetAgencyProfileActiveClients } from '../../../api/agencies/useGetAgencyProfileActiveClients'

type ActiveClientRowShape = {
  dealershipOrg: Dealership
  id: number
  dealership: string
  hasInventory: boolean
  url: string
  campaigns: number
  campaign_types: Array<string>
  gross_revenue: number
  demand_local_net: number
  clicks: number
  impressions: number
  views: number
  ctr: number
  vtr: number
  delivery: number
}

type AssociatedClientRowShape = {
  _id: string
  id: number
  dealership: string
  url: string
  created_date: string
  updated_date: string
}

const clientColumnHelper = createColumnHelper<ActiveClientRowShape>()
const associatedClientColumnHelper = createColumnHelper<AssociatedClientRowShape>()

export const AgencyDealerships = (): JSX.Element => {
  const { agency_id } = useParams()
  const { dateRange, setDateRange } = useDateRange()
  const [queryParams] = useSearchParams()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const user = useSelector(currentUserSelector)
  const navigate = useNavigate()
  const setDealership = useDealershipStore((state) => state.setDealership)

  const clientColumns = useMemo(() => {
    const baseColumns = [
      clientColumnHelper.accessor('dealership', {
        header: 'Name',
        cell: ({ row, getValue }) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={`/dashboard/agency/${agency_id}/dealership/${row.original.id}/profile`}
            onClick={() => setDealership(row.original.dealershipOrg)}>
            {getValue()}
          </Link>
        ),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('url', {
        header: 'Website URL',
        cell: ({ getValue }) => getValue(),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('campaigns', {
        header: 'Campaigns',
        cell: ({ getValue }) => formatNumber(getValue()),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('campaign_types', {
        header: 'Campaigns',
        cell: ({ getValue }) => (getValue() ?? []).join(', '),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue()),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue()),
        enableSorting: false,
      }),
      clientColumnHelper.accessor('views', {
        header: 'Views',
        cell: ({ getValue }) => formatNumber(getValue()),
        enableSorting: false,
      }),
    ]

    return baseColumns
  }, [agency_id, setDealership])

  const associatedClientColumns = useMemo(
    () => [
      associatedClientColumnHelper.accessor('dealership', {
        header: 'Client',
        cell: ({ getValue, row }) => (
          <Link
            to={`../../dealership/${row.original.id}/profile`}
            className="text-primary-persian-blue font-bold">
            {getValue()}
          </Link>
        ),
      }),
      associatedClientColumnHelper.accessor('url', {
        header: 'URL',
        cell: ({ getValue }) => getValue(),
      }),
      associatedClientColumnHelper.accessor('created_date', {
        header: 'Created Date',
        cell: ({ getValue }) => getValue(),
      }),
      associatedClientColumnHelper.accessor('updated_date', {
        header: 'Last Updated Date',
        cell: ({ getValue }) => getValue(),
      }),
      associatedClientColumnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          return (
            <div className="flex flex-row">
              <ActionEdit
                stroke={'#0932C2'}
                className={'ml-4 cursor-pointer'}
                onClick={() => {
                  navigate(`${info.row.original?.id}/edit`)
                }}
              />
            </div>
          )
        },
      }),
    ],
    [navigate],
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <span className="font-bold text-md">Active Clients</span>
        <DateRangePicker filters={filters} onChange={onDateRangeChange} />
      </div>
      <Table
        emptyLabel={'No Active Clients'}
        columns={clientColumns}
        customPageSize={10}
        filters={{
          agency_id: { value: agency_id },
          start_date: filters.dateRange.startDate,
          end_date: filters?.dateRange.endDate,
        }}
        fetchFn={useGetAgencyProfileDealerships}
        isDownloadable
      />
      <div className="flex items-center justify-between">
        <span className="font-bold text-md">Associated Clients</span>
      </div>
      <Table
        emptyLabel={'No Associated Clients'}
        columns={associatedClientColumns}
        customPageSize={10}
        filters={{
          agency_id: { value: agency_id },
        }}
        fetchFn={useGetAgencyProfileActiveClients}
        isDownloadable
      />
      <Outlet />
    </div>
  )
}

export default AgencyDealerships
